'use client';

import { useState } from 'react';
import type { JSX } from 'react';
import classNames from 'classnames';
import dynamic from 'next/dynamic';
import Tooltip from '@alltrails/denali/components/Tooltip';
import ThumbsUp from '@alltrails/denali/icons/ThumbsUp';
import ThumbsUpFilled from '@alltrails/denali/icons/ThumbsUpFilled';
import logReviewHelpfulVoteUpdated from '@alltrails/amplitude/events/logReviewHelpfulVoteUpdated';
import useUser from '@alltrails/context/hooks/useUser';
import type { Review } from '@alltrails/types/Review';
import { useVoteReviewMutation } from '../../slices/reviewsApi';
import { getRemoveVoteLink, getUpvoteLink } from '../../utils/getVoteLinks';
import styles from './ReviewActionSection.module.scss';

const SignUpModal = dynamic(() => import('@alltrails/user-auth/components/SignUpModal'), { ssr: false });

type ReviewVoteButtonProps = {
  review: Review;
  disabled?: boolean;
  readOnly?: boolean;
};

const ReviewVoteButton = ({ review, disabled = false, readOnly = false }: ReviewVoteButtonProps): JSX.Element | null => {
  const user = useUser();
  const [voteReview, { isLoading: isVoting }] = useVoteReviewMutation();
  const [isSignUpModalOpen, setIsSignUpModalOpen] = useState(false);

  const upvoteLink = getUpvoteLink(review);
  const removeVoteLink = getRemoveVoteLink(review);

  const isUpvoted = review.votes?.myVote?.type === 'upvote';
  const upvoteCount = review.votes?.upvoteCount ?? (review as { upvote_count?: number }).upvote_count ?? 0;

  const canVote = !!review.votes?.links?.length;

  const handleUpvote = () => {
    if (!user) {
      setIsSignUpModalOpen(true);
      return;
    }
    if (isVoting || !canVote) return;
    const voteAction = isUpvoted ? 'remove' : 'add';

    if (isUpvoted && removeVoteLink) {
      voteReview({ link: removeVoteLink, reviewId: review.id });
    } else if (upvoteLink) {
      voteReview({ link: upvoteLink, reviewId: review.id });
    }

    logReviewHelpfulVoteUpdated({
      review_id: String(review.id),
      trail_id: String(review.trailId ?? ''),
      vote_action: voteAction
    });
  };

  if (!canVote) {
    return null;
  }

  const likeLabel = isUpvoted ? 'Unmark as helpful' : 'Mark as helpful';

  const tooltipText = `${upvoteCount} ${upvoteCount === 1 ? 'person' : 'people'} found this helpful`;

  const voteContent = (
    <div className={styles.voteSection}>
      <button
        className={classNames(styles.likeButton, { [styles.readOnly]: readOnly })}
        onClick={handleUpvote}
        data-testid={`review-${review.id}-like-button`}
        type="button"
        title={readOnly ? undefined : likeLabel}
        aria-label={likeLabel}
        disabled={disabled || isVoting}
      >
        {isUpvoted ? (
          <ThumbsUpFilled data-testid={`review-${review.id}-thumbs-up-filled`} />
        ) : (
          <ThumbsUp color="--color-text-secondary" data-testid={`review-${review.id}-thumbs-up`} />
        )}
      </button>
      {upvoteCount > 0 && <span className={styles.upvoteCount}>{upvoteCount}</span>}
      {isSignUpModalOpen && (
        <SignUpModal
          autoClose
          closeModal={() => setIsSignUpModalOpen(false)}
          isOpen={isSignUpModalOpen}
          onSuccess={() => setIsSignUpModalOpen(false)}
          trigger="Review Trail"
          upsellTrigger="Review Trail"
        />
      )}
    </div>
  );

  if (readOnly) {
    return <Tooltip anchorElement={voteContent} text={tooltipText} testId={`review-${review.id}-vote-tooltip`} />;
  }

  return voteContent;
};

export default ReviewVoteButton;
